import { IImageBaseViewModel } from 'Types/Litium';
import { dpr } from 'Utils/dpr';
import { getNewDimensionsWithAspectRatio } from 'Utils/getNewDimensionsWithAspectRatio';
import tailwindBreakpoints from 'Utils/tailwindBreakpoints';

export type VideoPlayerPlaceholderSrcSet = Array<{
    breakpoint: string;
    width: number;
    height: number;
    src: string;
    altText: string;
}>;

interface IVideoThumbnail {
    videoPlaceholderSizes: VideoPlayerPlaceholderSrcSet;
    placeholderLarge: IImageBaseViewModel;
    placeholderSmall: IImageBaseViewModel;
    shouldLazyLoad: boolean;
    placeholderAlt: string;
    className?: string;
    hasStarted: boolean;
}

const VideoThumbnail = ({
    videoPlaceholderSizes,
    placeholderLarge,
    placeholderSmall,
    shouldLazyLoad,
    placeholderAlt,
    className = '',
    hasStarted,
}: IVideoThumbnail) => (
    <picture className={`-:relative ${className}`}>
        {videoPlaceholderSizes.map((item, index) => (
            <source
                key={index}
                width={item.width > 0 ? item.width : ''}
                height={item.height > 0 ? item.height : ''}
                media={`(min-width: ${item.breakpoint})`}
                srcSet={dpr(
                    `${item.src}?format=webp&fit=crop&width=${parseInt(
                        item.breakpoint
                    )}&height=${getNewDimensionsWithAspectRatio(
                        item.width,
                        item.height,
                        parseInt(item.breakpoint),
                        'height'
                    )}`
                )}
            />
        ))}
        {videoPlaceholderSizes.length > 0 ? (
            <img
                width={
                    videoPlaceholderSizes[0].width > 0
                        ? videoPlaceholderSizes[0].width
                        : ''
                }
                height={
                    videoPlaceholderSizes[0].height > 0
                        ? videoPlaceholderSizes[0].height
                        : ''
                }
                className={`h-full w-full object-cover transition-opacity delay-500 duration-layout ${
                    hasStarted ? 'opacity-0' : 'opacity-100'
                }`}
                src={`${
                    videoPlaceholderSizes[0].src
                }?format=webp&fit=crop&width=${parseInt(
                    videoPlaceholderSizes[0].breakpoint
                )}&height=${getNewDimensionsWithAspectRatio(
                    videoPlaceholderSizes[0].width,
                    videoPlaceholderSizes[0].height,
                    parseInt(videoPlaceholderSizes[0].breakpoint),
                    'height'
                )}`}
                srcSet={dpr(
                    `${
                        videoPlaceholderSizes[0].src
                    }?format=webp&fit=crop&width=${parseInt(
                        videoPlaceholderSizes[0].breakpoint
                    )}&height=${getNewDimensionsWithAspectRatio(
                        videoPlaceholderSizes[0].width,
                        videoPlaceholderSizes[0].height,
                        parseInt(videoPlaceholderSizes[0].breakpoint),
                        'height'
                    )}`
                )}
                alt={videoPlaceholderSizes[0].altText}
                loading={shouldLazyLoad ? 'lazy' : 'eager'}
            />
        ) : (
            <>
                <source
                    width={
                        placeholderLarge.width > 0 ? placeholderLarge.width : ''
                    }
                    height={
                        placeholderLarge.height > 0
                            ? placeholderLarge.height
                            : ''
                    }
                    media={`(min-width: ${tailwindBreakpoints.xxxl})`}
                    srcSet={dpr(
                        `${
                            placeholderLarge.src
                        }?format=webp&fit=crop&width=${parseInt(
                            tailwindBreakpoints.xxxl
                        )}&height=${getNewDimensionsWithAspectRatio(
                            placeholderLarge.width,
                            placeholderLarge.height,
                            parseInt(tailwindBreakpoints.xxxl),
                            'height'
                        )}`
                    )}
                />
                <source
                    width={
                        placeholderLarge.width > 0 ? placeholderLarge.width : ''
                    }
                    height={
                        placeholderLarge.height > 0
                            ? placeholderLarge.height
                            : ''
                    }
                    media={`(min-width: ${tailwindBreakpoints.xxl})`}
                    srcSet={dpr(
                        `${
                            placeholderLarge.src
                        }?format=webp&fit=crop&width=${parseInt(
                            tailwindBreakpoints.xxxl
                        )}&height=${getNewDimensionsWithAspectRatio(
                            placeholderLarge.width,
                            placeholderLarge.height,
                            parseInt(tailwindBreakpoints.xxxl),
                            'height'
                        )}`
                    )}
                />
                <source
                    width={
                        placeholderLarge.width > 0 ? placeholderLarge.width : ''
                    }
                    height={
                        placeholderLarge.height > 0
                            ? placeholderLarge.height
                            : ''
                    }
                    media={`(min-width: ${tailwindBreakpoints.xl})`}
                    srcSet={dpr(
                        `${
                            placeholderLarge.src
                        }?format=webp&fit=crop&width=${parseInt(
                            tailwindBreakpoints.xxl
                        )}&height=${getNewDimensionsWithAspectRatio(
                            placeholderLarge.width,
                            placeholderLarge.height,
                            parseInt(tailwindBreakpoints.xxl),
                            'height'
                        )}`
                    )}
                />
                <source
                    width={
                        placeholderLarge.width > 0 ? placeholderLarge.width : ''
                    }
                    height={
                        placeholderLarge.height > 0
                            ? placeholderLarge.height
                            : ''
                    }
                    media={`(min-width: ${tailwindBreakpoints.lg})`}
                    srcSet={dpr(
                        `${
                            placeholderLarge.src
                        }?format=webp&fit=crop&width=${parseInt(
                            tailwindBreakpoints.xl
                        )}&height=${getNewDimensionsWithAspectRatio(
                            placeholderLarge.width,
                            placeholderLarge.height,
                            parseInt(tailwindBreakpoints.xl),
                            'height'
                        )}`
                    )}
                />
                <source
                    width={
                        placeholderSmall.width > 0 ? placeholderSmall.width : ''
                    }
                    height={
                        placeholderSmall.height > 0
                            ? placeholderSmall.height
                            : ''
                    }
                    media={`(min-width: ${tailwindBreakpoints.md})`}
                    srcSet={dpr(
                        `${
                            placeholderSmall.src
                        }?format=webp&fit=crop&width=${parseInt(
                            tailwindBreakpoints.lg
                        )}&height=${getNewDimensionsWithAspectRatio(
                            placeholderSmall.width,
                            placeholderSmall.height,
                            parseInt(tailwindBreakpoints.lg),
                            'height'
                        )}`
                    )}
                />
                <img
                    width={
                        placeholderSmall.width > 0 ? placeholderSmall.width : ''
                    }
                    height={
                        placeholderSmall.height > 0
                            ? placeholderSmall.height
                            : ''
                    }
                    className="h-full w-full object-cover"
                    src={`${
                        placeholderSmall.src
                    }?format=webp&fit=crop&width=${parseInt(
                        tailwindBreakpoints.md
                    )}&height=${getNewDimensionsWithAspectRatio(
                        placeholderSmall.width,
                        placeholderSmall.height,
                        parseInt(tailwindBreakpoints.md),
                        'height'
                    )}`}
                    srcSet={dpr(
                        `${
                            placeholderSmall.src
                        }?format=webp&fit=crop&width=${parseInt(
                            tailwindBreakpoints.md
                        )}&height=${getNewDimensionsWithAspectRatio(
                            placeholderSmall.width,
                            placeholderSmall.height,
                            parseInt(tailwindBreakpoints.md),
                            'height'
                        )}`
                    )}
                    alt={placeholderAlt}
                    loading={shouldLazyLoad ? 'lazy' : 'eager'}
                />
            </>
        )}
    </picture>
);

export default VideoThumbnail;
